/// <reference path="../../../../global.d.ts" />

import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, inject, input, Input, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslatedDatepickerModule } from '@jfw/angular/shared/i18n';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { CustomTooltipModule } from '@jfw/angular/ecommerce/common-components/custom-tooltip';
import { StickyStackTopDirective } from '@jfw/angular/shared/utils-angular';
import { getStyleTypeFromCode, PRODUCT_PRIORITY } from '@jfw/business-logic';
import {
  EzTuxEnvironment,
  EzTuxParentApplication,
  OrderListItem,
  OrderListItemDetail,
  StyleType,
} from '@jfw/types';
import { Subject, Subscription } from 'rxjs';
import { WholesaleOrderListViewService } from '../service/order-list-view.service';
import { WholesaleTrackingInfoModalComponent } from '../tracking-info-modal/tracking-info-modal.component';

@Component({
  selector: 'jfw-wholesale-all-account-order-list-view',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    TranslatedDatepickerModule,
    MatCheckboxModule,
    MatTableModule,
    MatProgressSpinnerModule,
    CommonModule,
    MatIconModule,
    CustomTooltipModule,
    MatDialogModule,
    StickyStackTopDirective,
  ],
  templateUrl: './all-account-order-list-view.component.html',
  styleUrl: './all-account-order-list-view.component.scss',
})
export class WholesaleAllAccountOrderListViewComponent {
  private router = inject(Router);
  // New input property for direct orders
  @Input() receivedOrderList: OrderListItem[] | undefined = [];

  @Input() eztux_base_url: string = 'https://ezdevbo-1.jimsfw.com';

  @Input() order_search_api_server_url: string = '';

  // Subject for handling the direct input changes
  private directOrderSubject = new Subject<OrderListItem[] | undefined>();
  private btid: string | null = '';
  private dialog = inject(MatDialog);
  loaded = signal(false);
  error = signal('');
  form = new FormGroup({
    accountNumber: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
    fromDate: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
    toDate: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
  });

  initialLoad: boolean = true;
  csrMode: boolean = false;

  loadingOrderId: number | undefined;
  loadingOrderNumber: number | undefined;
  loadingEventId: string | undefined;

  @Input()
  set resetloading(value: string | boolean) {
    if (value !== undefined && (value === 'true' || value === true)) {
      this.initialLoad = value === true || value === 'true';
    }
  }

  @Input() parent_application: EzTuxParentApplication;
  @Input() environment: EzTuxEnvironment;
  @Input() newSite = false;

  private document = inject(DOCUMENT);
  private orderListSubscription: Subscription | undefined;

  orderListWrapper$ = this.orderListViewService.orderListWrapper$;

  // EXT-5972: opt-in sticky header pinning -- see StickyStackTopDirective's
  // own doc comment for why this defaults to off.
  public readonly stickyHeaderStackAbove = input<string>('');

  protected readonly SORT_KEYS = [
    'eventDate',
    'orderNumber',
    'customer',
    'eventDescription',
    'orderType',
  ] as const;
  protected readonly sortKey = signal<(typeof this.SORT_KEYS)[number]>('eventDate');
  protected readonly sortDirection = signal<1 | -1>(1);
  protected readonly limit = signal(50);

  // EXT-6023: in-results filter term, owned by the parent order-search
  // component (whose search-form row hosts the actual input) and passed down
  // here so the grid's own rows narrow to match Customer/Event/Account Name
  // case-insensitively. hasMore()/endLabel() below key off the filtered list
  // so "Load 50 more" stops showing once the filtered set is fully revealed.
  public readonly filterTerm = input<string>('');

  private filteredList(
    list: OrderListItem[] | undefined,
  ): OrderListItem[] | undefined {
    if (!list) return undefined;
    const term = this.filterTerm().trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (order) =>
        (order.fullName ?? '').toLowerCase().includes(term) ||
        (order.eventDescription ?? '').toLowerCase().includes(term) ||
        (order.shipToName ?? '').toLowerCase().includes(term),
    );
  }

  private sortValueOf(order: OrderListItem, key: (typeof this.SORT_KEYS)[number]) {
    switch (key) {
      case 'eventDate':
        return order.eventDate ?? '';
      case 'orderNumber':
        return order.orderNumber ?? 0;
      case 'customer':
        return order.fullName ?? '';
      case 'eventDescription':
        return order.eventDescription ?? '';
      case 'orderType':
        return order.orderType ?? '';
    }
  }

  private sortedList(
    list: OrderListItem[] | undefined,
  ): OrderListItem[] | undefined {
    if (!list) return undefined;
    const key = this.sortKey();
    const dir = this.sortDirection();
    return [...list].sort((a, b) => {
      const av = this.sortValueOf(a, key);
      const bv = this.sortValueOf(b, key);
      let cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      if (cmp === 0 && key === 'eventDate') {
        cmp = (a.orderNumber ?? 0) - (b.orderNumber ?? 0);
      }
      return cmp * dir;
    });
  }

  // EXT-5972: sorted+windowed view for the table's [dataSource] -- takes the
  // async-piped list directly (see the template) rather than a signal, since
  // this component's data already flows through orderListWrapper$ | async
  // rather than a local signal like the sibling order-list-view component.
  protected visibleOrderList(
    list: OrderListItem[] | undefined,
  ): OrderListItem[] | undefined {
    return this.sortedList(this.filteredList(list))?.slice(0, this.limit());
  }

  // EXT-6023: keyed off the filtered list, not the raw `list` param directly
  // -- otherwise "Load 50 more" stays visible, and the label stays wrong,
  // once a filter narrows the visible set below `limit` even though there's
  // nothing further to reveal.
  protected hasMore(list: OrderListItem[] | undefined): boolean {
    return this.limit() < (this.filteredList(list)?.length ?? 0);
  }

  protected endLabel(list: OrderListItem[] | undefined): string {
    const total = this.filteredList(list)?.length ?? 0;
    const shown = Math.min(this.limit(), total);
    return shown >= total
      ? `End of results · ${total} rows`
      : `Showing ${shown} of ${total}`;
  }

  protected sortBy(key: (typeof this.SORT_KEYS)[number]): void {
    if (this.sortKey() === key) {
      this.sortDirection.update((dir) => (dir === 1 ? -1 : 1) as 1 | -1);
    } else {
      this.sortKey.set(key);
      this.sortDirection.set(1);
    }
  }

  // EXT-6023: splits `text` into segments so the template can highlight the
  // portion(s) matching the active filter term (case-insensitive) without
  // resorting to [innerHTML]/sanitization.
  protected highlightSegments(
    text: string | null | undefined,
    term: string,
  ): { text: string; match: boolean }[] {
    const value = text ?? '';
    const needle = term.trim();
    if (!needle) return [{ text: value, match: false }];
    const lowerValue = value.toLowerCase();
    const lowerNeedle = needle.toLowerCase();
    const segments: { text: string; match: boolean }[] = [];
    let i = 0;
    while (i < value.length) {
      const idx = lowerValue.indexOf(lowerNeedle, i);
      if (idx === -1) {
        segments.push({ text: value.slice(i), match: false });
        break;
      }
      if (idx > i) segments.push({ text: value.slice(i, idx), match: false });
      segments.push({ text: value.slice(idx, idx + needle.length), match: true });
      i = idx + needle.length;
    }
    return segments;
  }

  protected loadMore(): void {
    this.limit.update((n) => n + 50);
  }

  constructor(private orderListViewService: WholesaleOrderListViewService) {}

  ngOnInit(): void {
    this.setCSRMode();

    this.orderListSubscription =
      this.orderListViewService.orderListWrapper$.subscribe((wrapper) => {
        if (wrapper.status === 'PENDING') {
          this.loaded.set(false);
          this.error.set('');
        } else if (wrapper.status === 'READY') {
          this.loaded.set(true);
          this.error.set('');
          // Update the receivedOrderList with the orders from the wrapper

          console.warn('Received orderList is undefined');
          // Only assign if wrapper.orderList is an array of OrderListItem
          if (
            Array.isArray(wrapper.orderList) &&
            wrapper.orderList.every((item) => 'internalOrderNumber' in item)
          ) {
            this.receivedOrderList = wrapper.orderList as OrderListItem[];
            this.limit.set(50);
          } else {
            this.receivedOrderList = undefined;
            console.warn('wrapper.orderList is not an OrderListItem[]');
          }

          // console.log('Received orders:', this.receivedOrderList);
        } else if (wrapper.status === 'ERROR') {
          this.loaded.set(true);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.orderListSubscription) {
      this.orderListSubscription.unsubscribe();
    }
  }

  setCSRMode() {
    /// If the parent application is dealerportal, set csrMode to false.
    if (this.parent_application === 'dealerportal') {
      this.csrMode = false;
      // console.warn('CSR Mode is disabled');
      return;
    }

    /// If the parent application is jfwplatformportal, set csrMode to true.
    if (this.parent_application === 'jfwplatformportal') {
      this.csrMode = true;
      // console.warn('CSR Mode is enabled');
      return;
    }

    // This must be eztuxportal, so we check the window.isCSRMode function.
    try {
      const csrModeString = window.isCSRMode();

      if (csrModeString === 'weWxer!@y2dfwdNewz@5RtS-!;D#2912hdh') {
        this.csrMode = true;
        // console.warn('CSR Mode is enabled');
      } else {
        this.csrMode = false;
        // console.warn('CSR Mode is disabled');
      }
    } catch (e) {
      // console.error('Error setting CSR mode:', e);
      this.csrMode = false;
      // console.warn('CSR Mode is disabled');
    }
  }

  redirectToEdit(details: OrderListItem): void {
    if (this.parent_application === 'jfwplatformportal') {
      const urlTree = this.router.createUrlTree(['/wholesale/order-entry'], {
        queryParams: {
          eventProductionWeek: details.eventProductionWeek,
          orderNumber: details.orderNumber,
          accountNumber: details.orderAccountNumber,
        },
      });
      const url = this.router.serializeUrl(urlTree);
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    } else if (this.parent_application === 'dealerportal') {
      this.router.navigate(['/order-entry'], {
        state: {
          eventProductionWeek: details.eventProductionWeek,
          orderNumber: details.orderNumber,
        },
      });
      return;
    }
    this.loadingOrderId = details.internalOrderNumber;

    const url = `${this.eztux_base_url}/memberV2/OrderEntryFromOmni.aspx?ACTNUM=${details.orderAccountNumber}&INTNUM=${details.internalOrderNumber}`;
    // window.open(url, '_blank', 'noopener');
    window.location.href = url;
  }

  redirectToReceipt(details: OrderListItem): void {
    if (this.parent_application === 'jfwplatformportal') {
      this.router.navigate(['/wholesale/receipt-viewer'], {
        state: { internalOrderNumber: details.internalOrderNumber },
        queryParams: { internalOrderNumber: details.internalOrderNumber },
      });
      return;
    } else if (this.parent_application === 'dealerportal' && this.newSite) {
      this.router.navigate(['/receipt-viewer'], {
        state: { internalOrderNumber: details.internalOrderNumber },
        queryParams: { internalOrderNumber: details.internalOrderNumber },
      });
      return;
    }

    const queryString = this.document.location.search;
    const urlParams = new URLSearchParams(queryString);
    const btid = urlParams.get('btid');
    console.log('btid:', btid);
    if (btid) {
      this.btid = btid;
      const url = `${this.eztux_base_url}/MemberV2/ReceiptPopup.aspx?INTNUM=${details.internalOrderNumber}&btid=${this.btid}`;
      window.open(url, '', 'noopener');
    }
    console.log('btid:', btid);
  }

  redirectToEventManager(details: OrderListItem): void {
    const queryString = this.document.location.search;
    const urlParams = new URLSearchParams(queryString);
    const btid = urlParams.get('btid');
    console.log('btid:', btid);
    if (this.parent_application === 'jfwplatformportal') {
      this.router.navigate(['/wholesale/event-planner'], {
        queryParams: { id: details.nationalEventId },
      });
      return;
    } else if (this.parent_application === 'dealerportal') {
      this.router.navigate(['/event-planner'], {
        queryParams: { id: details.nationalEventId },
      });
      return;
    }
    if (btid) {
      this.loadingEventId = details.nationalEventId;
      this.loadingOrderNumber = details.internalOrderNumber;
      this.btid = btid;
      const url = `${this.eztux_base_url}/memberv2/EventEntry.aspx?btid=${this.btid}&NATEVID=${details.nationalEventId}`;
      // window.open(url, '', 'noopener');
      window.location.href = url;
    }
    console.log('btid:', btid);
  }

  hasReplacement(order: OrderListItem): boolean {
    return order.details?.some((item) => item.replacementCode !== '') ?? false;
  }

  hasDirectDelivery(order: OrderListItem): boolean {
    return order.shipToCode === 3;
  }

  getReplacementToolTip(order: OrderListItem): string {
    let toolTipIterator: Array<Array<string>> = [];
    order.details?.forEach((detail) => {
      const lineItem = [];
      if (detail.replacementCode || detail.replacementCode !== '') {
        lineItem.push(
          getStyleTypeFromCode(detail.orderProduct) ?? detail.orderProduct,
        );
        /**
         * Shirts only have option 1 and 2 no 3
         */
        if (detail.orderProduct === 'SR') {
          lineItem.push(detail.orderStyle);
          lineItem.push(detail.orderOption1);
          lineItem.push(detail.orderOption2);
        } else {
          lineItem.push(detail.orderStyle);
          lineItem.push(detail.orderOption1 + detail.orderOption2);
          if (detail.orderOption3) lineItem.push(detail.orderOption3);
        }
        toolTipIterator.push(lineItem);
      }
    });
    toolTipIterator = toolTipIterator.sort((a, b) => {
      const aIndex = PRODUCT_PRIORITY.indexOf(a[0] as StyleType);
      const bIndex = PRODUCT_PRIORITY.indexOf(b[0] as StyleType);
      return aIndex - bIndex;
    });
    let grid = `
      <div style="
        display: grid;
        grid-template-columns:
        repeat(4, auto); gap: 4px;
      ">
        ${toolTipIterator
          .map((row) => {
            return (
              row.map((rowItem) => `<div>${rowItem}</div>`).join('') +
              '<div></div>'.repeat(4 - row.length)
            );
          })
          .join('')}
      </div>
    `;
    return grid;
  }

  getDirectDelieveryToolTip(order: OrderListItem): string {
    let address = '';
    const {
      firstName,
      lastName,
      shipToAddress1,
      shipToAddress2,
      shipToCity,
      shipToState,
      shipToZip,
    } = order;
    if (firstName && lastName) {
      address += `${firstName} ${lastName} <br/>`;
    }
    if (shipToAddress1) {
      address += shipToAddress1 + '<br/>';
    }
    if (shipToAddress2) {
      address += shipToAddress2 + '<br/>';
    }
    if (shipToCity && shipToState && shipToZip) {
      address += `${shipToCity}, ${shipToState}, ${shipToZip}`;
    }
    return address;
  }
  getShipToDiffActToolTip(order: OrderListItem): string {
    const {
      shipToAccountNumber,
      shipToAddress1,
      shipToName,
      shipToCity,
      shipToState,
      shipToZip,
    } = order;
    let address = '';
    if (shipToAccountNumber) {
      address += `${shipToAccountNumber}` + '<br/>';
    }
    if (shipToName) {
      address += shipToName + '<br/>';
    }
    if (shipToAddress1) {
      address += shipToAddress1 + '<br/>';
    }
    if (shipToCity && shipToState && shipToZip) {
      address += `${shipToCity}, ${shipToState}, ${shipToZip}`;
    }
    return address;
  }

  openModal(order: OrderListItem): void {
    this.dialog.open(WholesaleTrackingInfoModalComponent, {
      data: {
        order,
        apiUrl: this.order_search_api_server_url,
        csrMode: this.csrMode,
      },
      // ...modalSettings,
      width: '45%',
      minWidth: '30%',
      maxHeight: '100vh',
    });
  }

  setReceiptClass(order: OrderListItem): string {
    const balance = order.receiptBalance;
    const receiptNumber = order.receiptInternalOrderNumber;
    if (receiptNumber === -1) {
      return 'no-receipt';
    }
    if (balance > 0) {
      return 'unpaid';
    }
    if (balance === 0) {
      return 'paid';
    }
    return '';
  }

  // Use toSignal to convert the observable to a signal.
  // orders = toSignal(this.orders$, { initialValue: [] });
  displayedColumns = [
    'internalOrderNumber',
    'tracking',
    'receipt',
    'eventDate',
    'orderNumber',
    'customer',
    'eventDescription',
    'orderType',
    'caller',
    'accountNumber',
    'accountName',
    'city',
    'state',
  ] as const;

  orderHasShippingStatus(details: OrderListItemDetail[]): boolean {
    // Returns whether or not order has at least 1 fulfilled item.
    return details.some((detailItem) => detailItem.orderStatus === 'S');
  }
}
